import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { listStockLevels, listInventoryWarehouses } from '../../services/inventoryApi';
import { PageHeader, Card, Badge, SearchInput, Button } from '../ui';

const PAGE_SIZE = 50;

const fmtQty = (n) => {
  const num = Number(n);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
};
const fmtTime = (t) => (t ? new Date(t).toLocaleString() : '—');

const StockBadge = ({ row }) => {
  const ats = Number(row.available_to_sell);
  const min = Number(row.minimum_qty) || 0;
  if (!row.is_present) return <Badge variant="neutral">absent</Badge>;
  if (ats <= 0) return <Badge variant="danger">out of stock</Badge>;
  if (min > 0 && ats < min) return <Badge variant="warning">below minimum</Badge>;
  return <Badge variant="success">in stock</Badge>;
};

const StockLevels = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const company = searchParams.get('company') || '';
  const warehouse = searchParams.get('warehouse') || '';
  const search = searchParams.get('search') || '';
  const inStockOnly = searchParams.get('in_stock') === '1';
  const includeDisabled = searchParams.get('disabled') === '1';
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const sort = searchParams.get('sort') || 'part_num';
  const direction = searchParams.get('dir') || 'asc';

  const [data, setData] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [error, setError] = useState(null);
  const [searchDraft, setSearchDraft] = useState(search);

  const setParam = useCallback(
    (updates) => {
      const next = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([k, v]) => {
        if (v === '' || v == null || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    listInventoryWarehouses({ enabled_only: true })
      .then((rows) => setWarehouses(rows || []))
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setError(null);
    try {
      const params = { page, page_size: PAGE_SIZE, sort, direction };
      if (company) params.company = company;
      if (warehouse) params.warehouse = warehouse;
      if (search) params.search = search;
      if (inStockOnly) params.in_stock_only = true;
      if (includeDisabled) params.include_disabled = true;
      const res = await listStockLevels(params);
      setData(res);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
      setData(null);
    }
  }, [company, warehouse, search, inStockOnly, includeDisabled, page, sort, direction]);

  useEffect(() => {
    load();
  }, [load]);

  // Debounce the search box into the URL param.
  useEffect(() => {
    const id = setTimeout(() => {
      if (searchDraft !== search) setParam({ search: searchDraft, page: '' });
    }, 350);
    return () => clearTimeout(id);
  }, [searchDraft, search, setParam]);

  const onSort = (col) => {
    if (sort === col) setParam({ dir: direction === 'asc' ? 'desc' : 'asc', page: '' });
    else setParam({ sort: col, dir: 'asc', page: '' });
  };

  const sortMark = (col) => (sort === col ? (direction === 'asc' ? ' ▲' : ' ▼') : '');

  const warehouseOptions = warehouses.filter((w) => !company || w.company === company);
  // A URL-persisted warehouse filter may reference a warehouse that's since
  // been disabled (absent from the enabled-only list). Keep it visible so the
  // active filter isn't invisible.
  const selectedMissing =
    warehouse && !warehouseOptions.some((w) => w.warehouse_code === warehouse);
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <div className="sync-page stock-levels">
      <PageHeader
        title="Stock Levels"
        sub="Current per-SKU, per-warehouse stock buckets from Epicor. Available to sell counts firm unshipped sales orders (incl. held) as committed: on hand − (non-sales commitments + max(sales commitments, open demand)), floored at zero. Click a row for the full bucket breakdown and change history."
        actions={<Button variant="secondary" to="/inventory">Overview</Button>}
      />

      <Card>
        <div className="checkbox-row" style={{ gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
          <SearchInput
            value={searchDraft}
            onChange={setSearchDraft}
            placeholder="Search SKU — wildcards * and ? (244*, 244?1)"
          />
          <select
            value={company}
            onChange={(e) => setParam({ company: e.target.value, warehouse: '', page: '' })}
          >
            <option value="">All companies</option>
            <option value="AU">AU</option>
            <option value="NZ">NZ</option>
          </select>
          <select
            value={warehouse}
            onChange={(e) => setParam({ warehouse: e.target.value, page: '' })}
          >
            <option value="">All warehouses</option>
            {warehouseOptions.map((w) => (
              <option key={`${w.company}-${w.warehouse_code}`} value={w.warehouse_code}>
                {w.warehouse_code} — {w.description || w.company}
              </option>
            ))}
            {selectedMissing && (
              <option value={warehouse}>{warehouse} — disabled</option>
            )}
          </select>
          <label className="chip">
            <input
              type="checkbox"
              checked={inStockOnly}
              onChange={(e) => setParam({ in_stock: e.target.checked ? '1' : '', page: '' })}
            />
            In stock only
          </label>
          <label className="chip">
            <input
              type="checkbox"
              checked={includeDisabled}
              onChange={(e) => setParam({ disabled: e.target.checked ? '1' : '', page: '' })}
            />
            Show disabled warehouses
          </label>
        </div>
      </Card>

      {error && <div className="banner error">{error}</div>}

      <section className="card">
        <table>
          <thead>
            <tr>
              <th style={{ cursor: 'pointer' }} onClick={() => onSort('part_num')}>SKU{sortMark('part_num')}</th>
              <th>Co</th>
              <th style={{ cursor: 'pointer' }} onClick={() => onSort('warehouse_code')}>Warehouse{sortMark('warehouse_code')}</th>
              <th style={{ cursor: 'pointer', textAlign: 'right' }} onClick={() => onSort('available_to_sell')}>
                Available to sell{sortMark('available_to_sell')}
              </th>
              <th style={{ cursor: 'pointer', textAlign: 'right' }} onClick={() => onSort('on_hand_qty')}>
                On hand{sortMark('on_hand_qty')}
              </th>
              <th style={{ textAlign: 'right' }}>Reserved</th>
              <th style={{ textAlign: 'right' }}>Allocated</th>
              <th style={{ textAlign: 'right' }}>Picking</th>
              <th style={{ textAlign: 'right' }}>Picked</th>
              <th
                style={{ cursor: 'pointer', textAlign: 'right' }}
                onClick={() => onSort('open_demand_qty')}
                title="Open unshipped sales order demand (held subset in parentheses)"
              >
                Open demand{sortMark('open_demand_qty')}
              </th>
              <th style={{ textAlign: 'right' }}>Demand</th>
              <th>Status</th>
              <th style={{ cursor: 'pointer' }} onClick={() => onSort('synced_at')}>Synced{sortMark('synced_at')}</th>
            </tr>
          </thead>
          <tbody>
            {!data?.items?.length && (
              <tr><td colSpan={13} className="muted">No stock rows for this filter.</td></tr>
            )}
            {(data?.items || []).map((r) => (
              <tr
                key={`${r.company}-${r.plant}-${r.warehouse_code}-${r.part_num}`}
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/inventory/stock/${r.company}/${encodeURIComponent(r.part_num)}`)}
              >
                <td><strong>{r.part_num}</strong></td>
                <td>{r.company}</td>
                <td>{r.warehouse_code}</td>
                <td style={{ textAlign: 'right' }}>
                  <strong>{fmtQty(r.available_to_sell)}</strong>
                  {Number(r.available_raw) < 0 && (
                    <span className="muted" title="Raw availability is negative — Epicor PartWhse drift signal">
                      {' '}({fmtQty(r.available_raw)})
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.on_hand_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.reserved_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.allocated_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.picking_qty)}</td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.picked_qty)}</td>
                <td style={{ textAlign: 'right' }}>
                  {fmtQty(r.open_demand_qty)}
                  {Number(r.open_demand_held_qty) > 0 && (
                    <span className="muted" title="Held-order subset of open demand">
                      {' '}({fmtQty(r.open_demand_held_qty)} held)
                    </span>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>{fmtQty(r.demand_qty)}</td>
                <td><StockBadge row={r} /></td>
                <td className="muted">{fmtTime(r.synced_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {data && (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8 }}>
            <button
              type="button"
              className="btn"
              disabled={page <= 1}
              onClick={() => setParam({ page: page - 1 })}
            >
              ‹ Prev
            </button>
            <span className="muted">
              Page {page} of {totalPages.toLocaleString()} — {Number(data.total).toLocaleString()} rows
            </span>
            <button
              type="button"
              className="btn"
              disabled={page >= totalPages}
              onClick={() => setParam({ page: page + 1 })}
            >
              Next ›
            </button>
          </div>
        )}
      </section>
    </div>
  );
};

export default StockLevels;
